import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getScheduleCReport,
  getTransactions,
  exportTransactionsCSV,
  exportTransactionsExcel,
  exportScheduleCCSV,
  exportScheduleCPDF,
  getMileageEntries
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import {
  FileDown,
  FileSpreadsheet,
  FileText,
  BarChart3,
  PieChart as PieChartIcon,
  Table,
  DollarSign,
  TrendingUp,
  Calendar,
  CalendarDays,
  CalendarRange,
  Loader2,
  Download
} from 'lucide-react';

const CHART_COLORS = [
  'hsl(173, 79%, 39%)',
  'hsl(142, 72%, 45%)',
  'hsl(188, 76%, 52%)',
  'hsl(37, 92%, 50%)',
  'hsl(0, 84%, 60%)',
  'hsl(262, 83%, 58%)',
  'hsl(221, 83%, 53%)',
];

// Radio button component
function RadioOption({ selected, onChange, label, color = 'accent' }) {
  const colorClasses = {
    accent: selected ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]' : 'border-muted-foreground/30',
    green: selected ? 'border-emerald-500 bg-emerald-500' : 'border-muted-foreground/30',
    red: selected ? 'border-red-500 bg-red-500' : 'border-muted-foreground/30',
    blue: selected ? 'border-blue-500 bg-blue-500' : 'border-muted-foreground/30',
    purple: selected ? 'border-purple-500 bg-purple-500' : 'border-muted-foreground/30',
    orange: selected ? 'border-orange-500 bg-orange-500' : 'border-muted-foreground/30',
    yellow: selected ? 'border-yellow-500 bg-yellow-500' : 'border-muted-foreground/30',
    cyan: selected ? 'border-cyan-500 bg-cyan-500' : 'border-muted-foreground/30',
  };
  
  const textColor = {
    accent: 'text-[hsl(var(--accent))]',
    green: 'text-emerald-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
    purple: 'text-purple-400',
    orange: 'text-orange-400',
    yellow: 'text-yellow-400',
    cyan: 'text-cyan-400',
  };

  return (
    <button
      type="button"
      onClick={onChange}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all hover:bg-muted/50 ${selected ? 'bg-muted/30' : ''}`}
    >
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${colorClasses[color] || colorClasses.accent}`}>
        {selected && <div className="w-2 h-2 rounded-full bg-background" />}
      </div>
      <span className={`font-medium ${selected ? (textColor[color] || textColor.accent) : 'text-foreground'}`}>{label}</span>
    </button>
  );
}

// Period option with icon
function PeriodOption({ selected, onChange, label, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all border ${
        selected 
          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]' 
          : 'border-border hover:border-[hsl(var(--accent))]/50 hover:bg-muted/30'
      }`}
    >
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
        selected ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]' : 'border-muted-foreground/30'
      }`}>
        {selected && <div className="w-2 h-2 rounded-full bg-background" />}
      </div>
      <Icon className="w-4 h-4" />
      <span className="font-medium">{label}</span>
    </button>
  );
}

const StatCard = ({ title, value, icon: Icon, subtitle }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold mt-1 font-['Space_Grotesk'] tnum">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="p-2.5 rounded-lg bg-[rgba(20,184,166,0.1)]">
          <Icon className="w-5 h-5 text-[hsl(var(--accent))]" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ScheduleCTable = ({ data }) => (
  <div className="overflow-x-auto">
    <table className="data-table">
      <thead>
        <tr>
          <th>Schedule C Line</th>
          <th className="text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(data).map(([line, amount]) => (
          <tr key={line}>
            <td>{line}</td>
            <td className="text-right font-['Space_Grotesk'] tnum">
              ${amount.toLocaleString()}
            </td>
          </tr>
        ))}
        <tr className="bg-muted/50 font-semibold">
          <td>Total Expenses</td>
          <td className="text-right font-['Space_Grotesk'] tnum text-[hsl(var(--success))]">
            ${Object.values(data).reduce((sum, v) => sum + v, 0).toLocaleString()}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

export default function Reports({ context }) {
  const location = useLocation();
  const dataContext = context || (location.pathname.startsWith('/personal') ? 'personal' : 'business');
  
  // Report generation state
  const [reportType, setReportType] = useState('all');
  const [reportPeriod, setReportPeriod] = useState('annual');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());
  const [exportFormat, setExportFormat] = useState('pdf');
  
  // Data state
  const [scheduleC, setScheduleC] = useState({ schedule_c: {}, total: 0 });
  const [categoryData, setCategoryData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [activeTab, setActiveTab] = useState('generate');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const year = parseInt(selectedYear);
      const [scheduleCRes, transactionsRes, mileageRes] = await Promise.all([
        getScheduleCReport(year),
        getTransactions({ category: 'business', limit: 1000 }),
        getMileageEntries(year)
      ]);

      setScheduleC(scheduleCRes.data);

      const categories = {};
      (transactionsRes.data.transactions || []).forEach(t => {
        const cat = t.tax_category || 'Uncategorized';
        categories[cat] = (categories[cat] || 0) + Math.abs(t.amount);
      });

      if (mileageRes.data.total_deduction > 0) {
        categories['Mileage'] = mileageRes.data.total_deduction;
      }

      setCategoryData(
        Object.entries(categories)
          .map(([name, value]) => ({ name, value: Math.round(value) }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 7)
      );

      const monthly = {};
      (transactionsRes.data.transactions || [])
        .filter(t => t.date?.startsWith(year.toString()))
        .forEach(t => {
          const month = t.date.substring(0, 7);
          monthly[month] = (monthly[month] || 0) + Math.abs(t.amount);
        });

      setMonthlyData(
        Object.entries(monthly)
          .map(([month, amount]) => ({
            month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short' }),
            amount: Math.round(amount)
          }))
          .sort((a, b) => a.month.localeCompare(b.month))
      );

    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  function getDateRange() {
    const year = parseInt(selectedYear);
    const month = parseInt(selectedMonth);
    
    if (reportPeriod === 'annual') {
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    } else if (reportPeriod === 'monthly') {
      const lastDay = new Date(year, month, 0).getDate();
      return {
        start: `${year}-${String(month).padStart(2, '0')}-01`,
        end: `${year}-${String(month).padStart(2, '0')}-${lastDay}`
      };
    } else {
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    }
  }

  function getReportDescription() {
    const reportLabels = {
      all: 'comprehensive',
      income: 'income',
      expenses: 'expense',
      profit_loss: 'profit & loss',
      tax_summary: 'tax summary',
      mileage: 'mileage',
      category: 'category breakdown',
      schedule_c: 'Schedule C'
    };
    const typeLabel = reportLabels[reportType] || '';
    const monthName = months.find(m => m.value === selectedMonth)?.label || '';
    
    if (reportPeriod === 'annual') {
      return `Generate an annual ${typeLabel} report for ${selectedYear}.`;
    } else if (reportPeriod === 'monthly') {
      return `Generate a monthly ${typeLabel} report for ${monthName} ${selectedYear}.`;
    } else {
      return `Generate a ${typeLabel} report for ${selectedYear}.`;
    }
  }

  const handleExport = async (type, format) => {
    setExporting(`${type}-${format}`);
    try {
      let res;
      let filename;
      const year = parseInt(selectedYear);

      if (type === 'transactions') {
        if (format === 'csv') {
          res = await exportTransactionsCSV({ category: 'business', year });
          filename = `${dataContext}_transactions_${year}.csv`;
        } else {
          res = await exportTransactionsExcel({ category: 'business', year });
          filename = `${dataContext}_transactions_${year}.xlsx`;
        }
      } else if (type === 'schedule-c') {
        if (format === 'csv') {
          res = await exportScheduleCCSV(year);
          filename = `schedule_c_${year}.csv`;
        } else {
          res = await exportScheduleCPDF(year);
          filename = `schedule_c_${year}.pdf`;
        }
      }

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const handleGenerateReport = async () => {
    const periodLabel = reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1);
    setExporting('generate');
    try {
      const year = parseInt(selectedYear);
      let res;
      let filename;

      if (exportFormat === 'pdf') {
        res = await exportScheduleCPDF(year);
        filename = `${dataContext}_${reportPeriod}_report_${year}.pdf`;
      } else if (exportFormat === 'csv') {
        res = await exportTransactionsCSV({ year });
        filename = `${dataContext}_${reportPeriod}_report_${year}.csv`;
      } else {
        res = await exportTransactionsExcel({ year });
        filename = `${dataContext}_${reportPeriod}_report_${year}.xlsx`;
      }

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${periodLabel} report downloaded!`);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">
          {dataContext === 'personal' ? 'Personal' : 'Business'} Reports
        </h1>
        <p className="text-muted-foreground mt-1">Schedule C and financial reports</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          title="Total Deductions"
          value={`$${scheduleC.total?.toLocaleString() || '0'}`}
          icon={DollarSign}
          subtitle={`${selectedYear} Tax Year`}
        />
        <StatCard
          title="Categories"
          value={Object.keys(scheduleC.schedule_c || {}).length}
          icon={BarChart3}
          subtitle="Schedule C lines"
        />
        <StatCard
          title="Estimated Savings"
          value={`$${((scheduleC.total || 0) * 0.25).toLocaleString()}`}
          icon={TrendingUp}
          subtitle="at 25% tax rate"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted">
          <TabsTrigger value="generate">
            <Download className="w-4 h-4 mr-2" /> Generate Reports
          </TabsTrigger>
          <TabsTrigger value="charts">
            <BarChart3 className="w-4 h-4 mr-2" /> Charts
          </TabsTrigger>
          <TabsTrigger value="schedule-c">
            <Table className="w-4 h-4 mr-2" /> Schedule C
          </TabsTrigger>
        </TabsList>

        {/* Generate Reports Tab */}
        <TabsContent value="generate">
          <Card className="card-premium overflow-hidden">
            <CardHeader className="border-b border-border bg-muted/30">
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[hsl(var(--accent))]" />
                Generate Period Reports
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Create weekly, monthly, or annual reports for income, expenses, or both
              </p>
            </CardHeader>
            
            <CardContent className="p-6 space-y-6">
              {/* Report Type */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Report Type</Label>
                <div className="flex flex-wrap gap-2">
                  <RadioOption
                    selected={reportType === 'all'}
                    onChange={() => setReportType('all')}
                    label="All Transactions"
                    color="accent"
                  />
                  <RadioOption
                    selected={reportType === 'income'}
                    onChange={() => setReportType('income')}
                    label="Income Only"
                    color="green"
                  />
                  <RadioOption
                    selected={reportType === 'expenses'}
                    onChange={() => setReportType('expenses')}
                    label="Expenses Only"
                    color="red"
                  />
                  <RadioOption
                    selected={reportType === 'profit_loss'}
                    onChange={() => setReportType('profit_loss')}
                    label="Profit & Loss"
                    color="blue"
                  />
                  <RadioOption
                    selected={reportType === 'tax_summary'}
                    onChange={() => setReportType('tax_summary')}
                    label="Tax Summary"
                    color="purple"
                  />
                  <RadioOption
                    selected={reportType === 'mileage'}
                    onChange={() => setReportType('mileage')}
                    label="Mileage Log"
                    color="orange"
                  />
                  <RadioOption
                    selected={reportType === 'category'}
                    onChange={() => setReportType('category')}
                    label="By Category"
                    color="cyan"
                  />
                  <RadioOption
                    selected={reportType === 'schedule_c'}
                    onChange={() => setReportType('schedule_c')}
                    label="Schedule C"
                    color="yellow"
                  />
                </div>
              </div>

              {/* Report Period */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Report Period</Label>
                <div className="flex flex-wrap gap-3">
                  <PeriodOption
                    selected={reportPeriod === 'weekly'}
                    onChange={() => setReportPeriod('weekly')}
                    label="Weekly"
                    icon={Calendar}
                  />
                  <PeriodOption
                    selected={reportPeriod === 'monthly'}
                    onChange={() => setReportPeriod('monthly')}
                    label="Monthly"
                    icon={CalendarDays}
                  />
                  <PeriodOption
                    selected={reportPeriod === 'annual'}
                    onChange={() => setReportPeriod('annual')}
                    label="Annual"
                    icon={CalendarRange}
                  />
                </div>
              </div>

              {/* Selectors */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(year => (
                        <SelectItem key={year} value={year}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {reportPeriod === 'monthly' && (
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map(month => (
                          <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Format</Label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="excel">Excel</SelectItem>
                      <SelectItem value="csv">CSV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div className="p-4 rounded-lg bg-muted/50 border border-border">
                <p className="text-sm text-muted-foreground">
                  {getReportDescription().split(reportPeriod).map((part, i, arr) => 
                    i < arr.length - 1 ? (
                      <span key={i}>
                        {part}
                        <span className="font-semibold text-foreground">{reportPeriod}</span>
                      </span>
                    ) : part
                  )}
                </p>
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerateReport}
                disabled={!!exporting}
                className="w-full h-12 text-base font-semibold bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-black"
              >
                {exporting === 'generate' ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Generate {reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)} Report
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Quick Export */}
          <Card className="card-premium mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[hsl(var(--accent))]" />
                Quick Export
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Download common report formats instantly
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Button
                  variant="outline"
                  onClick={() => handleExport('transactions', 'csv')}
                  disabled={!!exporting}
                  className="h-auto py-4 flex-col gap-2 border-blue-500/30 hover:bg-blue-500/10 hover:border-blue-500"
                >
                  {exporting === 'transactions-csv' ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <FileText className="w-6 h-6 text-blue-500" />
                  )}
                  <span className="font-medium">Transactions CSV</span>
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => handleExport('transactions', 'excel')}
                  disabled={!!exporting}
                  className="h-auto py-4 flex-col gap-2 border-emerald-500/30 hover:bg-emerald-500/10 hover:border-emerald-500"
                >
                  {exporting === 'transactions-excel' ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-6 h-6 text-emerald-500" />
                  )}
                  <span className="font-medium">Transactions Excel</span>
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => handleExport('schedule-c', 'csv')}
                  disabled={!!exporting}
                  className="h-auto py-4 flex-col gap-2 border-purple-500/30 hover:bg-purple-500/10 hover:border-purple-500"
                >
                  {exporting === 'schedule-c-csv' ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <FileText className="w-6 h-6 text-purple-500" />
                  )}
                  <span className="font-medium">Schedule C CSV</span>
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => handleExport('schedule-c', 'pdf')}
                  disabled={!!exporting}
                  className="h-auto py-4 flex-col gap-2 border-red-500/30 hover:bg-red-500/10 hover:border-red-500"
                >
                  {exporting === 'schedule-c-pdf' ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <FileText className="w-6 h-6 text-red-500" />
                  )}
                  <span className="font-medium">Schedule C PDF</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Charts Tab */}
        <TabsContent value="charts">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="w-5 h-5 text-[hsl(var(--accent))]" />
                  Expenses by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-64 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[hsl(var(--accent))]" />
                  Monthly Expenses
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-64 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : monthlyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        formatter={(value) => `$${value.toLocaleString()}`}
                        contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                      />
                      <Bar dataKey="amount" fill="hsl(173, 79%, 39%)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Schedule C Tab */}
        <TabsContent value="schedule-c">
          <Card className="card-premium">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Table className="w-5 h-5 text-[hsl(var(--accent))]" />
                  Schedule C Summary - {selectedYear}
                </CardTitle>
                <Button 
                  onClick={() => window.location.href = '/business/schedule-c'}
                  className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-black"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Full Schedule C Preview
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : Object.keys(scheduleC.schedule_c || {}).length > 0 ? (
                <ScheduleCTable data={scheduleC.schedule_c} />
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No Schedule C data available for {selectedYear}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
